// T224 — Auth and wallet analytics event definitions.
// Each event definition is a plain object with a `name` and a TypeScript
// phantom type for its properties, enforced via the EventDefinition generic.

import type { EventDefinition } from './navigation';

// ---------------------------------------------------------------------------
// Auth event property types
// ---------------------------------------------------------------------------

export type LogoutProperties = {
  user_id: string;
};

export type LogoutErrorProperties = {
  error: string;
};

export type SessionRestoredProperties = {
  user_id: string;
};

export type SessionRestoreErrorProperties = {
  error: string;
};

export type WalletCreatedProperties = {
  wallet_type: string;
};

export type WalletConnectedProperties = {
  wallet_type: string;
  address: string;
};

export type WalletRenamedProperties = {
  wallet_id: string;
};

export type WalletUnlinkedProperties = {
  wallet_id: string;
  wallet_type: string;
};

export type WalletSwitchedProperties = {
  from_wallet_id: string | null;
  to_wallet_id: string;
};

export type ProfileUpdatedProperties = {
  fields_changed: string[];
};

export type ProfileViewedProperties = {
  user_id: string;
};

export type AccountDeletedProperties = {
  user_id: string;
};

// ---------------------------------------------------------------------------
// Auth event definitions
// ---------------------------------------------------------------------------

export const LOGOUT: EventDefinition<LogoutProperties> = {
  name: 'logout',
};

export const LOGOUT_ERROR: EventDefinition<LogoutErrorProperties> = {
  name: 'logout_error',
};

export const SESSION_RESTORED: EventDefinition<SessionRestoredProperties> = {
  name: 'session_restored',
};

export const SESSION_RESTORE_ERROR: EventDefinition<SessionRestoreErrorProperties> = {
  name: 'session_restore_error',
};

export const WALLET_CREATED: EventDefinition<WalletCreatedProperties> = {
  name: 'wallet_created',
};

export const WALLET_CONNECTED: EventDefinition<WalletConnectedProperties> = {
  name: 'wallet_connected',
};

export const WALLET_RENAMED: EventDefinition<WalletRenamedProperties> = {
  name: 'wallet_renamed',
};

export const WALLET_UNLINKED: EventDefinition<WalletUnlinkedProperties> = {
  name: 'wallet_unlinked',
};

export const WALLET_SWITCHED: EventDefinition<WalletSwitchedProperties> = {
  name: 'wallet_switched',
};

export const PROFILE_UPDATED: EventDefinition<ProfileUpdatedProperties> = {
  name: 'profile_updated',
};

export const PROFILE_VIEWED: EventDefinition<ProfileViewedProperties> = {
  name: 'profile_viewed',
};

export const ACCOUNT_DELETED: EventDefinition<AccountDeletedProperties> = {
  name: 'account_deleted',
};

// ---------------------------------------------------------------------------
// Locale event definitions
// ---------------------------------------------------------------------------

export type LocaleChangedProperties = {
  from_locale: string;
  to_locale: string;
};

export const LOCALE_CHANGED: EventDefinition<LocaleChangedProperties> = {
  name: 'locale_changed',
};

// ---------------------------------------------------------------------------
// Location event definitions
// ---------------------------------------------------------------------------

export type LocationChangedProperties = {
  country: string;
  source: 'manual' | 'ip';
};

export const LOCATION_CHANGED: EventDefinition<LocationChangedProperties> = {
  name: 'location_changed',
};

// ---------------------------------------------------------------------------
// Currency event definitions
// ---------------------------------------------------------------------------

export type CurrencyChangedProperties = {
  from: string;
  to: string;
};

export const CURRENCY_CHANGED: EventDefinition<CurrencyChangedProperties> = {
  name: 'currency_changed',
};

// ---------------------------------------------------------------------------
// Auth method + error event definitions (T056 — Turnkey auth migration)
// ---------------------------------------------------------------------------

/**
 * Auth method values aligned with Turnkey SDK's AuthMethod enum.
 * - otp: email one-time password / magic link
 * - oauth: Google, X (Twitter), Apple — provider-level granularity not
 *   exposed by Turnkey's onAuthenticationSuccess callback
 * - wallet: external Solana wallet (Phantom, Solflare, Backpack)
 * - passkey: WebAuthn passkey (currently disabled in our config)
 */
export type AuthMethod = 'otp' | 'oauth' | 'wallet' | 'passkey';

export type AuthMethodUsedProperties = {
  method: AuthMethod;
  isNewUser: boolean;
};

export const AUTH_METHOD_USED: EventDefinition<AuthMethodUsedProperties> = {
  name: 'auth_method_used',
};

export type AuthErrorProperties = {
  error: string;
  method: AuthMethod | null;
  /** Where in the auth flow the error surfaced — e.g. 'turnkey_on_error',
   *  'complete_oauth_login' (the SDK Turnkey provider emits both). */
  context?: string;
  /** OAuth provider when known (x, google, apple). */
  provider?: string | null;
  /** Error class name (Error/TurnkeyError/...). */
  name?: string;
  /** Flattened `Error.cause` when present — the actual failure detail. */
  cause?: string;
};

export const AUTH_ERROR: EventDefinition<AuthErrorProperties> = {
  name: 'auth_error',
};

/**
 * Fired when the `/auth/callback` page is still showing the "verifying" spinner
 * past the hang threshold — i.e. the OAuth completion (Turnkey token exchange →
 * Morfi session stamp → navigate) did not finish in time. `phase` distinguishes
 * the first telemetry beacon from the later forced self-redirect. The context
 * fields pinpoint where the flow died (was a code present? did Turnkey ever mint
 * a session → `is_authenticated`?).
 */
export type AuthCallbackHangProperties = {
  provider: string | null;
  flow: string | null;
  elapsed_ms: number;
  had_code: boolean;
  had_id_token: boolean;
  is_authenticated: boolean;
  phase: 'telemetry' | 'redirect';
};

export const AUTH_CALLBACK_HANG: EventDefinition<AuthCallbackHangProperties> = {
  name: 'auth_callback_hang',
};

/** Fired when the Turnkey SDK session expires and automatic refresh fails. */
export type TurnkeySessionExpiredProperties = Record<string, never>;

export const TURNKEY_SESSION_EXPIRED: EventDefinition<TurnkeySessionExpiredProperties> = {
  name: 'turnkey_session_expired',
};

/** Fired when the session steward proactively re-mints the Turnkey session
 *  (rolling refresh on load/foreground). `trigger` = 'ready' | 'foreground' | 'online'. */
export type TurnkeySessionRefreshedProperties = {
  trigger: string;
  /** What happened to the key the refresh retired: deleted, absent, failed or skipped. */
  pruned: string;
};

export const TURNKEY_SESSION_REFRESHED: EventDefinition<TurnkeySessionRefreshedProperties> = {
  name: 'turnkey_session_refreshed',
};

/** Fired when the steward's proactive refresh fails — the kit internally
 *  logs itself out on refresh failure, so a spike here precedes reconcile logouts. */
export type TurnkeyRefreshFailedProperties = {
  trigger: string;
  reason: string;
};

export const TURNKEY_REFRESH_FAILED: EventDefinition<TurnkeyRefreshFailedProperties> = {
  name: 'turnkey_refresh_failed',
};

/** Fired when the steward detects the mixed state (Morfi-authed user with a
 *  dead Turnkey signing session) and fans out the silent logout. Should trend
 *  to ~zero once the TTL raise + rolling refresh are live. */
export type TurnkeySessionReconcileLogoutProperties = {
  trigger: string;
};

export const TURNKEY_SESSION_RECONCILE_LOGOUT: EventDefinition<TurnkeySessionReconcileLogoutProperties> =
  {
    name: 'turnkey_session_reconcile_logout',
  };

/** Chrome popup OAuth return skipped WalletKit so the opener can complete.
 *  `had_code` / `had_id_token` are booleans only — never the tokens. */
export type TurnkeyPopupCallbackSkipProperties = {
  had_code: boolean;
  had_id_token: boolean;
};

export const TURNKEY_POPUP_CALLBACK_SKIP: EventDefinition<TurnkeyPopupCallbackSkipProperties> = {
  name: 'turnkey_popup_callback_skip',
};

/** One proactive Morfi-session refresh attempt from the SDK scheduler.
 *  `trigger`: timer / visibility / online. `outcome`: refreshed / rejected
 *  (session gone) / deferred (transport or server error, retried later). */
export type SessionRefreshProperties = {
  trigger: string;
  outcome: string;
};

export const SESSION_REFRESH: EventDefinition<SessionRefreshProperties> = {
  name: 'session_refresh',
};

/**
 * A wallet the signed-in session cannot reach: its key lives in another
 * Turnkey sub-organization, so the balance is real and on chain but nothing
 * here can sign for it. `count` is how many such wallets the reconcile saw.
 * The sub-org ids stay off the metric on purpose — they identify a person and
 * would widen label cardinality without telling us anything we can act on.
 */
export type WalletUnreachableSubOrgProperties = {
  count: number;
  sessionSubOrg: string;
  accountSubOrg: string;
};

export const WALLET_UNREACHABLE_SUB_ORG: EventDefinition<WalletUnreachableSubOrgProperties> = {
  name: 'wallet_unreachable_sub_org',
};

/**
 * The reconcile declined to touch the wallet list because this session sits on
 * a sub-organization the account does not point at. Its wallets are not this
 * account's to add, and the account's own are not this session's to judge.
 */
export type WalletReconcileSkippedForeignSubOrgProperties = {
  sessionSubOrg: string;
  accountSubOrg: string;
};

export const WALLET_RECONCILE_SKIPPED_FOREIGN_SUB_ORG: EventDefinition<WalletReconcileSkippedForeignSubOrgProperties> =
  {
    name: 'wallet_reconcile_skipped_foreign_sub_org',
  };

/** A private-key / wallet export attempt through the Turnkey modal.
 *  `flow`: settings (wallet list) or account_deletion (mandatory pre-delete
 *  export). `result`: exported / cancelled / error. */
export type WalletExportProperties = {
  flow: 'settings' | 'account_deletion';
  result: 'exported' | 'cancelled' | 'error';
};

export const WALLET_EXPORT: EventDefinition<WalletExportProperties> = {
  name: 'wallet_export',
};
